import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import Iconify from '../Iconify';
import { getRecentFeedbackSends } from '../../utils/recentFeedbackSends';

const SERIF_STACK = "'DM Serif Display', 'Playfair Display', 'Cormorant Garamond', Georgia, 'Times New Roman', serif";
const STAR_GOLD = '#E8A33D';

/**
 * Patient Feedback card surfaced on the Reviews & Feedback Overview tab.
 *
 * Two states:
 *   1. EMPTY    — practice hasn't sent any feedback links yet. Shows the
 *                 "Catch unhappy patients before Google does" teaser + CTA.
 *   2. ACTIVE   — at least one link has been sent (sessionStorage-tracked
 *                 until backend exposes engagement send history). Shows the
 *                 most recent sends + visual guidance on what happens next.
 */
export default function PatientFeedbackOverviewCard({ onOpen }) {
    const theme = useTheme();
    const primaryMain = theme.palette.primary.main;
    const primaryDark = theme.palette.primary.dark;
    const practiceId = useSelector((s) => s.practiceTreatmentList?.practiceId);

    // Hydrate from sessionStorage on mount + when practice switches.
    // Refresh on focus so a fresh send in another tab is reflected here.
    const [recentSends, setRecentSends] = useState(() => getRecentFeedbackSends(practiceId));

    useEffect(() => {
        setRecentSends(getRecentFeedbackSends(practiceId));
        const refresh = () => setRecentSends(getRecentFeedbackSends(practiceId));
        window.addEventListener('focus', refresh);
        return () => window.removeEventListener('focus', refresh);
    }, [practiceId]);

    const hasActivity = recentSends.length > 0;

    if (!hasActivity) {
        return <EmptyState primaryMain={primaryMain} primaryDark={primaryDark} onOpen={onOpen} />;
    }

    return <ActiveState theme={theme} primaryMain={primaryMain} primaryDark={primaryDark} sends={recentSends} onOpen={onOpen} />;
}

PatientFeedbackOverviewCard.propTypes = {
    onOpen: PropTypes.func.isRequired,
};

// ──────────────────────────────────────────────────────────────────────────

function EmptyState({ primaryMain, primaryDark, onOpen }) {
    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(primaryMain, 0.04)} 0%, ${alpha(primaryMain, 0.08)} 100%)`,
                border: '1px solid',
                borderColor: alpha(primaryMain, 0.15),
                px: { xs: 3, md: 5 },
                py: { xs: 3.5, md: 4 },
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.35,
                    backgroundImage: `radial-gradient(circle at 1px 1px, ${alpha(primaryMain, 0.18)} 1px, transparent 0)`,
                    backgroundSize: '20px 20px',
                    pointerEvents: 'none',
                }}
            />

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 3, md: 4 }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ position: 'relative' }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        width: 92,
                        height: 92,
                        borderRadius: 2.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: alpha(primaryMain, 0.2),
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: `0 12px 24px -16px ${alpha(primaryMain, 0.3)}`,
                    }}
                >
                    <Stack spacing={0.5} alignItems="center">
                        <Stack direction="row" spacing={0.15}>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Iconify key={i} icon="solar:star-bold" width={12} sx={{ color: STAR_GOLD }} />
                            ))}
                        </Stack>
                        <Iconify icon="solar:chat-square-like-bold-duotone" width={32} sx={{ color: primaryDark }} />
                    </Stack>
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            display: 'inline-block',
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '2px',
                            color: primaryDark,
                            textTransform: 'uppercase',
                            mb: 1,
                            px: 1,
                            py: 0.25,
                            bgcolor: alpha(primaryMain, 0.12),
                            borderRadius: '999px',
                        }}
                    >
                        New · Patient Feedback
                    </Typography>

                    <Typography
                        component="h2"
                        sx={{
                            fontFamily: SERIF_STACK,
                            fontSize: { xs: 22, sm: 26, md: 30 },
                            lineHeight: 1.15,
                            color: 'text.primary',
                            fontWeight: 400,
                            letterSpacing: '-0.3px',
                            mb: 1,
                        }}
                    >
                        Catch unhappy patients
                        <Box component="span" sx={{ fontStyle: 'italic', color: primaryDark }}>
                            {' '}before Google does.
                        </Box>
                    </Typography>

                    <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: 'text.secondary', maxWidth: 560 }}>
                        Send a private feedback link after every appointment. 4 and 5-star patients are routed to your Google review page. Anything lower stays here so you can fix it first.
                    </Typography>
                </Box>

                <Box sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', md: 'center' } }}>
                    <Button
                        size="large"
                        variant="contained"
                        onClick={onOpen}
                        endIcon={<Iconify icon="solar:arrow-right-linear" width={18} />}
                        fullWidth
                        sx={{
                            px: 3,
                            py: 1.25,
                            fontSize: 14,
                            fontWeight: 600,
                            borderRadius: '999px',
                            whiteSpace: 'nowrap',
                            boxShadow: `0 8px 20px -8px ${alpha(primaryMain, 0.55)}`,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 12px 24px -8px ${alpha(primaryMain, 0.65)}`,
                            },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        Open Patient Feedback
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
}

EmptyState.propTypes = {
    primaryMain: PropTypes.string.isRequired,
    primaryDark: PropTypes.string.isRequired,
    onOpen: PropTypes.func.isRequired,
};

// ──────────────────────────────────────────────────────────────────────────

function ActiveState({ theme, primaryMain, primaryDark, sends, onOpen }) {
    const latest = sends[0];
    const olderCount = Math.max(0, sends.length - 1);
    const timeAgo = formatDistanceToNow(new Date(latest.sentAt), { addSuffix: true });

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: alpha(primaryMain, 0.18),
                boxShadow: `0 1px 3px ${alpha(theme.palette.grey[900], 0.04)}`,
            }}
        >
            {/* Accent stripe */}
            <Box
                sx={{
                    height: 4,
                    background: `linear-gradient(90deg, ${primaryMain} 0%, ${alpha(primaryMain, 0.4)} 100%)`,
                }}
            />

            <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={{ xs: 3, lg: 4 }}
                    alignItems={{ xs: 'flex-start', lg: 'center' }}
                >
                    {/* Left: latest send confirmation */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <Box
                                sx={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    bgcolor: alpha(theme.palette.success.main, 0.15),
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Iconify icon="solar:check-circle-bold" width={16} sx={{ color: 'success.dark' }} />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '2px',
                                    color: 'success.dark',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Link sent
                            </Typography>
                        </Stack>

                        <Typography
                            sx={{
                                fontFamily: SERIF_STACK,
                                fontSize: { xs: 22, md: 26 },
                                lineHeight: 1.2,
                                color: 'text.primary',
                                fontWeight: 400,
                                letterSpacing: '-0.3px',
                                mb: 0.75,
                            }}
                        >
                            Waiting on
                            <Box component="span" sx={{ fontStyle: 'italic', color: primaryDark }}>
                                {' '}{latest.leadName}
                            </Box>
                            {olderCount > 0 && (
                                <Typography component="span" sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: 'text.secondary' }}>
                                    {' '}and {olderCount} other{olderCount === 1 ? '' : 's'}
                                </Typography>
                            )}
                        </Typography>

                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.25 }}>
                            Last link sent {timeAgo}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            When they rate their experience, their feedback lands in the <strong>Patient Feedback</strong> tab.
                            4 and 5-stars are funnelled to your Google review page automatically.
                        </Typography>
                    </Box>

                    {/* Middle: flow visualisation */}
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                            flexShrink: 0,
                            p: 2,
                            bgcolor: alpha(primaryMain, 0.04),
                            border: '1px dashed',
                            borderColor: alpha(primaryMain, 0.25),
                            borderRadius: 2,
                        }}
                    >
                        <FlowStep
                            icon="solar:plain-bold"
                            label="Sent"
                            active
                            theme={theme}
                            primaryMain={primaryMain}
                            primaryDark={primaryDark}
                        />
                        <Iconify icon="solar:arrow-right-linear" width={14} sx={{ color: 'text.disabled' }} />
                        <FlowStep
                            icon="solar:star-bold"
                            label="Rate"
                            pending
                            theme={theme}
                            primaryMain={primaryMain}
                            primaryDark={primaryDark}
                        />
                        <Iconify icon="solar:arrow-right-linear" width={14} sx={{ color: 'text.disabled' }} />
                        <FlowStep
                            icon="logos:google-icon"
                            label="Google"
                            pending
                            theme={theme}
                            primaryMain={primaryMain}
                            primaryDark={primaryDark}
                            useColoredIcon
                        />
                    </Stack>

                    {/* Right: action */}
                    <Box sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', lg: 'center' } }}>
                        <Button
                            variant="contained"
                            onClick={onOpen}
                            endIcon={<Iconify icon="solar:arrow-right-linear" width={18} />}
                            fullWidth
                            sx={{
                                px: 3,
                                py: 1.25,
                                fontSize: 14,
                                fontWeight: 600,
                                borderRadius: '999px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            View Patient Feedback
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
}

ActiveState.propTypes = {
    theme: PropTypes.object.isRequired,
    primaryMain: PropTypes.string.isRequired,
    primaryDark: PropTypes.string.isRequired,
    sends: PropTypes.array.isRequired,
    onOpen: PropTypes.func.isRequired,
};

// ──────────────────────────────────────────────────────────────────────────

function FlowStep({ icon, label, active, pending, theme, primaryDark, useColoredIcon }) {
    const bg = active ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.grey[500], 0.12);
    let iconColor = primaryDark;
    if (active) iconColor = 'success.dark';
    else if (pending) iconColor = 'text.disabled';
    return (
        <Stack alignItems="center" spacing={0.5} sx={{ minWidth: 48 }}>
            <Box
                sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: bg,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Iconify icon={icon} width={useColoredIcon ? 18 : 16} sx={useColoredIcon ? undefined : { color: iconColor }} />
            </Box>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: active ? 'success.dark' : 'text.disabled', letterSpacing: '0.3px' }}>
                {label}
            </Typography>
        </Stack>
    );
}

FlowStep.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    active: PropTypes.bool,
    pending: PropTypes.bool,
    theme: PropTypes.object.isRequired,
    primaryDark: PropTypes.string.isRequired,
    useColoredIcon: PropTypes.bool,
};
